import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Borrowed-chord lexicon entries — copy authored by Bowie in
 * `.claude/agent-memory/bowie/reference_theory_copy_register.md` under
 * "Borrowed-chord copy". Kept in a separate file from `lexicon-entries.ts`
 * so Bowie's polished Phase-1 starter set (cadences/modes/progressions) stays
 * untouched and these new entries are explicitly attributable to Bowie's
 * reference document.
 *
 * Category: `'borrowed'` — added in Phase 1.5 (analyzer-lexicon consolidation)
 * to give borrowed-chord modal-interchange entries a distinct category from
 * the generic diatonic-function entries in `lexicon-function-entries.ts`.
 *
 * Naming note: lexicon ids encode `borrowed-{degree}-{scale-quality}`. The
 * `flat-VII` form (rather than `b7` or `bVII`) keeps ids ASCII-safe for file
 * tools and grep without losing meaning — the displayed copy uses Unicode.
 *
 * Phase-1 coverage covers the five most-common major-key borrowings. Minor-
 * key borrowings (e.g., the Dorian-flavoured IV in natural minor) are
 * intentionally not shipped here; the analyzer's `getParallelModeLabel`
 * returns null for them, so surfaces will gracefully hide the row.
 */
export const BORROWED_LEXICON_ENTRIES: LexiconEntry[] = [
  {
    id: 'borrowed-flat-VII-major',
    category: 'borrowed',
    displayName: '♭VII (borrowed from Mixolydian)',
    displayNameShort: '♭VII',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Borrowed sound — adds a rockier color than the key alone gives you.',
      intermediate: 'Borrowed from parallel Mixolydian — the ♭7 gives the rock/folk color.',
      advanced: 'Modal interchange — ♭VII borrowed from parallel Mixolydian.',
    },
    tagLabel: {
      beginner: 'borrowed',
      intermediate: 'borrowed ♭VII',
      advanced: '♭VII',
    },
    genreTags: ['rock', 'folk', 'pop'],
  },
  {
    // The dominant-seventh ♭VII (B♭7 in C): its A♭ is Aeolian, so it is the
    // backdoor dominant from the parallel minor, not Mixolydian color (GH #2164).
    id: 'borrowed-flat-VII7-major',
    category: 'borrowed',
    displayName: '♭VII7 (backdoor dominant)',
    displayNameShort: '♭VII7',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Borrowed sound — a soft, bluesy way home instead of the usual V.',
      intermediate: 'Borrowed from parallel minor — ♭VII7 slides home to I, a gentler pull than V.',
      advanced: 'Backdoor dominant — ♭VII7 from parallel minor; ♭6→5 and ♭7→1 resolve to I, often after iv7.',
    },
    tagLabel: {
      beginner: 'borrowed',
      intermediate: 'backdoor ♭VII7',
      advanced: '♭VII7',
    },
    genreTags: ['jazz', 'soul', 'pop'],
  },
  {
    id: 'borrowed-iv-major',
    category: 'borrowed',
    displayName: 'iv (borrowed from parallel minor)',
    displayNameShort: 'iv',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Borrowed sound — adds a soft melancholy.',
      intermediate: 'Borrowed from parallel minor — the minor IV gives a tender pull.',
      advanced: 'Modal interchange — iv borrowed from parallel minor; plagal-minor.',
    },
    tagLabel: {
      beginner: 'borrowed',
      intermediate: 'borrowed iv',
      advanced: 'iv',
    },
    genreTags: ['pop', 'ballad'],
  },
  {
    id: 'borrowed-flat-III-major',
    category: 'borrowed',
    displayName: '♭III (borrowed from parallel minor)',
    displayNameShort: '♭III',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Borrowed sound — drops in a warmer, solemn chord from the parallel minor.',
      intermediate: 'Borrowed from parallel minor — major chord built on the lowered third.',
      advanced: 'Modal interchange — ♭III borrowed from parallel minor; common in rock and cinematic harmony.',
    },
    tagLabel: {
      beginner: 'borrowed',
      intermediate: 'borrowed ♭III',
      advanced: '♭III',
    },
    genreTags: ['rock', 'pop', 'cinematic'],
  },
  {
    id: 'borrowed-flat-VI-major',
    category: 'borrowed',
    displayName: '♭VI (borrowed from parallel minor)',
    displayNameShort: '♭VI',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'Borrowed sound — a tender, often cinematic darker chord.',
      intermediate: 'Borrowed from parallel minor — major chord built on the lowered sixth.',
      advanced: 'Modal interchange — ♭VI borrowed from parallel minor; lowered-submediant color.',
    },
    tagLabel: {
      beginner: 'borrowed',
      intermediate: 'borrowed ♭VI',
      advanced: '♭VI',
    },
    genreTags: ['cinematic', 'pop'],
  },
  {
    id: 'borrowed-flat-II-major',
    category: 'borrowed',
    displayName: '♭II (Neapolitan / Phrygian color)',
    displayNameShort: '♭II',
    pedagogicalLevel: 3,
    copy: {
      beginner: 'Borrowed sound — adds a dramatic, Spanish-flavored color.',
      intermediate: 'Borrowed from parallel Phrygian — the ♭2 gives a dramatic half-step pull home.',
      advanced: 'Neapolitan — ♭II borrowed from parallel Phrygian; classical predominant.',
    },
    tagLabel: {
      beginner: 'borrowed',
      intermediate: 'Neapolitan',
      advanced: '♭II',
    },
    genreTags: ['flamenco', 'classical', 'film'],
  },
]
